import { zodResolver } from '@hookform/resolvers/zod'
import {
  ApiError,
  type CreateTaxCategoryInput,
  changePasswordSchema,
  createTaxCategorySchema,
  queryKeys,
  type StoreSettingsInput,
  type TaxCategory,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Monitor, Percent, Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Page, PageHeader } from '../components/app-shell.js'
import { BillingCalendarCard } from '../components/billing-calendar-settings.js'
import { IntegrationsTab } from '../components/integration-settings.js'
import { NotificationSettingsTab } from '../components/notification-settings.js'
import { RentalSettingsTab } from '../components/rental-settings.js'
import { ShabbosModeCard } from '../components/shabbos-mode-settings.js'
import { SupportSettingsTab } from '../components/support-settings.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface StaffOption {
  id: string
  name: string
  role: string
  isActive: boolean
}

interface SessionRow {
  id: string
  ip: string | null
  userAgent: string | null
  createdAt: string
  lastSeenAt: string
  current: boolean
}

export function SettingsPage() {
  const { staff, can } = useAuth()
  const canManageShop = can('settings:manage')
  const canManageIntegrations = can('integrations:manage')
  const canGetAlerts = can('support:read') || can('workspace:read')

  return (
    <Page>
      <PageHeader
        title="Settings"
        description={
          canManageShop ? 'The shop, your account and security.' : 'Your account and security.'
        }
      />

      <Tabs defaultValue={canManageShop ? 'shop' : 'account'}>
        <TabsList>
          {canManageShop ? <TabsTrigger value="shop">Shop</TabsTrigger> : null}
          {canManageShop ? <TabsTrigger value="support">Support</TabsTrigger> : null}
          {canManageShop ? <TabsTrigger value="rentals">Rentals</TabsTrigger> : null}
          {canManageIntegrations ? (
            <TabsTrigger value="integrations">Integrations</TabsTrigger>
          ) : null}
          <TabsTrigger value="account">Account</TabsTrigger>
          {canGetAlerts ? <TabsTrigger value="notifications">Notifications</TabsTrigger> : null}
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        {canManageShop ? (
          <TabsContent value="shop" className="space-y-5">
            <StoreSettingsCard />
            <ShabbosModeCard />
            <BillingCalendarCard />
            <SalesTaxCard />
            <RepairLabourSettingsCard />
            <ProvisioningFilterKindsCard />
            <TabletUpdatesCard />
            <CheckoutErrorCapturesCard />
            <VendingAdminCodeCard />
          </TabsContent>
        ) : null}

        {canManageShop ? (
          <TabsContent value="support">
            <SupportSettingsTab />
          </TabsContent>
        ) : null}

        {canManageShop ? (
          <TabsContent value="rentals">
            <RentalSettingsTab />
          </TabsContent>
        ) : null}

        {canManageIntegrations ? (
          <TabsContent value="integrations">
            <IntegrationsTab />
          </TabsContent>
        ) : null}

        <TabsContent value="account">
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle>Your account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-border pb-2">
                <span className="text-foreground-muted">Name</span>
                <span className="font-medium">{staff?.name}</span>
              </div>
              <div className="flex justify-between border-b border-border pb-2">
                <span className="text-foreground-muted">Email</span>
                <span className="font-medium">{staff?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-muted">Role</span>
                <Badge tone="primary">{staff?.role}</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {canGetAlerts ? (
          <TabsContent value="notifications">
            <NotificationSettingsTab />
          </TabsContent>
        ) : null}

        <TabsContent value="security" className="space-y-5">
          <ChangePasswordCard />
          <SessionsCard />
        </TabsContent>
      </Tabs>
    </Page>
  )
}

function ChangePasswordCard() {
  const toast = useToast()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof changePasswordSchema>>({
    resolver: zodResolver(changePasswordSchema),
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      await api.post('/auth/password', values)
      toast.success('Password changed', 'Your other sessions have been signed out.')
      reset()
    } catch (error) {
      toast.error(
        'Could not change your password',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Change password</CardTitle>
        <CardDescription>
          Changing it signs out every other device, which is what you want if you suspect someone
          else has your password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Current password" error={errors.currentPassword?.message} required>
            {(props) => (
              <Input
                {...props}
                {...register('currentPassword')}
                type="password"
                autoComplete="current-password"
                invalid={!!errors.currentPassword}
              />
            )}
          </Field>
          <Field
            label="New password"
            error={errors.newPassword?.message}
            hint="At least 12 characters. A memorable phrase beats a short jumble."
            required
          >
            {(props) => (
              <Input
                {...props}
                {...register('newPassword')}
                type="password"
                autoComplete="new-password"
                invalid={!!errors.newPassword}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            Change password
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function SessionsCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: sessions, isLoading } = useQuery({
    queryKey: queryKeys.sessions(),
    queryFn: () => api.get<SessionRow[]>('/auth/sessions'),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/sessions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions() })
      toast.success('Session signed out')
    },
    onError: (error) =>
      toast.error(
        'Could not sign that session out',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Where you are signed in</CardTitle>
        <CardDescription>
          If you see something you do not recognise, sign it out and change your password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-foreground-muted">Loading…</p>
        ) : (
          <ul className="space-y-2">
            {sessions?.map((session) => (
              <li
                key={session.id}
                className="flex items-center gap-3 rounded-md border border-border p-2.5"
              >
                <Monitor className="size-4 shrink-0 text-foreground-muted" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">
                    {session.userAgent?.split(')')[0]?.split('(')[1] ?? 'Unknown device'}
                  </p>
                  <p className="text-xs text-foreground-muted">
                    {session.ip ?? 'unknown address'} · last active{' '}
                    {new Date(session.lastSeenAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                {session.current ? (
                  <Badge tone="success">This device</Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revoke.mutate(session.id)}
                    loading={revoke.isPending}
                  >
                    Sign out
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {sessions && sessions.length === 1 ? (
          <Alert tone="info" className="mt-3">
            You are only signed in here.
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * Shop-wide settings that change how the storefront reads to a customer.
 *
 * Deliberately a small screen. Anything a customer has already been quoted —
 * tax, postage — is not editable here: those are stamped onto an order when it
 * is placed, and a control that could rewrite them afterwards would be a
 * control that changes what somebody was charged.
 */
function StoreSettingsCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.storeSettings(),
    queryFn: () => api.get<StoreSettingsInput>('/settings/store'),
  })

  const [threshold, setThreshold] = useState('')

  useEffect(() => {
    if (data) setThreshold(String(data.lowStockThreshold))
  }, [data])

  const save = useMutation({
    mutationFn: () => api.patch('/settings/store', { lowStockThreshold: Number(threshold) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeSettings() })
      toast.success('Saved', 'The storefront picks this up within a minute.')
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const parsed = Number(threshold)
  const invalid = !Number.isInteger(parsed) || parsed < 0 || parsed > 1000
  const unchanged = data ? parsed === data.lowStockThreshold : true

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Storefront</CardTitle>
        <CardDescription>How stock is described to customers on the website.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field
          label="Warn when stock drops to"
          hint="At or below this many, a product says “only a few left” instead of “in stock” on the website (zero switches that off). It also sets the default low-stock line in Stock and on the dashboard for any product without its own reorder point."
          error={invalid ? 'Enter a whole number between 0 and 1000' : undefined}
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              min={0}
              max={1000}
              inputMode="numeric"
              disabled={isLoading}
              value={threshold}
              invalid={invalid}
              onChange={(e) => setThreshold(e.target.value)}
            />
          )}
        </Field>

        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={invalid || unchanged || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * t1365: what repair labour costs by the hour, and which service item it is
 * sold as at the till. The item's tax category is what taxes labour, so a
 * shop that taxes labour differently from goods just files the item under that
 * category on the Products screen.
 */
function RepairLabourSettingsCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.storeSettings(),
    queryFn: () => api.get<StoreSettingsInput>('/settings/store'),
  })
  const services = useQuery({
    queryKey: queryKeys.products.list({ type: 'service', isArchived: false, forLabour: true }),
    queryFn: () =>
      api.get<{ items: { id: string; name: string; isActive: boolean }[] }>('/products', {
        query: { type: 'service', isArchived: false, pageSize: 100 },
      }),
  })

  const [rate, setRate] = useState('')
  const [productId, setProductId] = useState('')

  useEffect(() => {
    if (data) {
      setRate((data.repairLabourRateCents / 100).toFixed(2))
      setProductId(data.repairLabourProductId ?? '')
    }
  }, [data])

  const save = useMutation({
    mutationFn: () =>
      api.patch('/settings/store', {
        repairLabourRateCents: Math.round(Number(rate) * 100),
        repairLabourProductId: productId || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeSettings() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all() })
      toast.success('Saved')
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const parsed = Number(rate)
  const invalid = rate.trim() === '' || !Number.isFinite(parsed) || parsed < 0 || parsed > 1000
  const unchanged = data
    ? Math.round(parsed * 100) === data.repairLabourRateCents &&
      (productId || null) === data.repairLabourProductId
    : true

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Repair labour</CardTitle>
        <CardDescription>
          Charge for technician time by the hour. Log time on a repair, then use “Charge labour”.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field
          label="Default hourly rate ($)"
          hint="Filled in when time is logged on a repair; it can be changed per entry."
          error={invalid ? 'Enter an amount between 0 and 1000' : undefined}
        >
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              disabled={isLoading}
              value={rate}
              invalid={invalid}
              onChange={(e) => setRate(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Sold as"
          hint="A service item, rung up at the till for the labour total. Its tax category decides how labour is taxed — create one under Products (type Service) if you do not have one."
        >
          {(props) => (
            <Select
              value={productId || 'none'}
              onValueChange={(v) => setProductId(v === 'none' ? '' : v)}
            >
              <SelectTrigger {...props}>
                <SelectValue placeholder="Choose a service item" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not set</SelectItem>
                {(services.data?.items ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={invalid || unchanged || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * t1402: the filter kinds a technician picks from when a phone moves through
 * the provisioning queue. The move dialog also allows "Other…", so an
 * unlisted kind never blocks work.
 */
function ProvisioningFilterKindsCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.storeSettings(),
    queryFn: () => api.get<StoreSettingsInput>('/settings/store'),
  })

  const [kinds, setKinds] = useState<string[]>([])
  const [draft, setDraft] = useState('')

  useEffect(() => {
    if (data) setKinds(data.provisioningFilterKinds)
  }, [data])

  const save = useMutation({
    mutationFn: () => api.patch('/settings/store', { provisioningFilterKinds: kinds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeSettings() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.filterKinds() })
      toast.success('Saved')
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const trimmed = draft.trim()
  const duplicate = kinds.some((k) => k.toLowerCase() === trimmed.toLowerCase())
  const unchanged = data
    ? JSON.stringify(kinds) === JSON.stringify(data.provisioningFilterKinds)
    : true

  function add() {
    if (!trimmed || duplicate) return
    setKinds((current) => [...current, trimmed])
    setDraft('')
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Filter kinds</CardTitle>
        <CardDescription>
          When a phone is moved forward in the provisioning queue, the technician says whether a
          filter was put on and picks which kind from this list.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {kinds.length === 0 ? (
            <p className="text-sm text-foreground-muted">No kinds yet — add the ones you use.</p>
          ) : (
            kinds.map((k) => (
              <Badge key={k} className="gap-1">
                {k}
                <button
                  type="button"
                  aria-label={`Remove ${k}`}
                  onClick={() => setKinds((current) => current.filter((x) => x !== k))}
                >
                  <X className="size-3" />
                </button>
              </Badge>
            ))
          )}
        </div>
        <div className="flex gap-2">
          <Input
            aria-label="New filter kind"
            placeholder="e.g. Talk only"
            disabled={isLoading}
            maxLength={60}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                add()
              }
            }}
          />
          <Button variant="secondary" onClick={add} disabled={!trimmed || duplicate}>
            Add
          </Button>
        </div>
        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={unchanged || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Sales tax rates (t0947) — a managed list rather than a single figure,
 * because physical products and services commonly need to be taxed
 * differently. Assigned to a product from the Products screen, or left
 * unassigned to keep it non-taxable. A rate change here only ever affects
 * orders placed after the change — what a customer already paid is stamped
 * onto their order and never rewritten.
 */
function SalesTaxCard() {
  const { can } = useAuth()
  const canWrite = can('settings:manage')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<TaxCategory | null>(null)
  const [deleting, setDeleting] = useState<TaxCategory | null>(null)

  const { data: categories, isLoading } = useQuery({
    queryKey: queryKeys.taxCategories(),
    queryFn: () => api.get<TaxCategory[]>('/tax-categories'),
  })

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <div>
          <CardTitle>Sales tax</CardTitle>
          <CardDescription>
            The rates products and services can be taxed at. Rate changes affect new orders only —
            placed orders keep the tax they were charged.
          </CardDescription>
        </div>
        {canWrite ? (
          <Button variant="secondary" size="sm" onClick={() => setCreating(true)}>
            <Plus /> New rate
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? null : categories && categories.length > 0 ? (
          <ul className="divide-y divide-border">
            {categories.map((category) => (
              <li key={category.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{category.name}</p>
                  <p className="text-2xs text-foreground-muted">
                    {category.productCount} product{category.productCount === 1 ? '' : 's'}
                  </p>
                </div>
                <Badge tone="neutral">{(category.rateBps / 100).toFixed(2)}%</Badge>
                {canWrite ? (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(category)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleting(category)}>
                      Delete
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Percent}
              title="No tax rates yet"
              description="Every product is non-taxable until a rate is added and assigned to it."
              action={
                canWrite ? { label: 'Add a rate', onClick: () => setCreating(true) } : undefined
              }
            />
          </div>
        )}
      </CardContent>

      <CreateTaxCategoryDialog open={creating} onOpenChange={setCreating} />
      <EditTaxCategoryDialog category={editing} onClose={() => setEditing(null)} />
      <DeleteTaxCategoryDialog
        category={deleting}
        others={(categories ?? []).filter((c) => c.id !== deleting?.id)}
        onClose={() => setDeleting(null)}
      />
    </Card>
  )
}

type TaxCategoryFormValues = z.input<typeof createTaxCategorySchema>

function CreateTaxCategoryDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TaxCategoryFormValues, unknown, CreateTaxCategoryInput>({
    resolver: zodResolver(createTaxCategorySchema),
    defaultValues: { rateBps: 0 },
  })

  useEffect(() => {
    if (!open) reset({ rateBps: 0 })
  }, [open, reset])

  const onSubmit = handleSubmit(async (values) => {
    try {
      await api.post('/tax-categories', values)
      await queryClient.invalidateQueries({ queryKey: queryKeys.taxCategories() })
      toast.success('Tax rate created')
      onOpenChange(false)
    } catch (error) {
      toast.error(
        'Could not create the tax rate',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  })

  const ratePercent = watch('rateBps') != null ? (watch('rateBps') as number) / 100 : 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New tax rate</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <Field
              label="Name"
              error={errors.name?.message}
              required
              hint="e.g. “Products” or “Services”."
            >
              {(props) => <Input {...props} {...register('name')} invalid={!!errors.name} />}
            </Field>
            <Field label="Rate" error={errors.rateBps?.message} hint="As a percentage, e.g. 8.25.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  value={ratePercent || ''}
                  invalid={!!errors.rateBps}
                  onChange={(e) =>
                    setValue('rateBps', Math.round(Number(e.target.value || 0) * 100), {
                      shouldValidate: true,
                    })
                  }
                />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Create rate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditTaxCategoryDialog({
  category,
  onClose,
}: {
  category: TaxCategory | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [ratePercent, setRatePercent] = useState('0')

  useEffect(() => {
    if (!category) return
    setName(category.name)
    setRatePercent((category.rateBps / 100).toString())
  }, [category])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/tax-categories/${category?.id}`, {
        name: name.trim(),
        rateBps: Math.round(Number(ratePercent || 0) * 100),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.taxCategories() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={category !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {category?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Rate" hint="As a percentage, e.g. 8.25. Affects new orders only.">
            {(props) => (
              <Input
                {...props}
                type="number"
                step="0.01"
                min="0"
                max="20"
                value={ratePercent}
                onChange={(e) => setRatePercent(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Non-taxable is a real choice here, not just "no category" — a product
 *  filed under the deleted rate has to land somewhere, and non-taxable is a
 *  fine, honest answer to "somewhere". */
const NON_TAXABLE_MOVE = 'none'

function DeleteTaxCategoryDialog({
  category,
  others,
  onClose,
}: {
  category: TaxCategory | null
  others: TaxCategory[]
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [moveTo, setMoveTo] = useState<string>('')

  useEffect(() => setMoveTo(''), [])

  const occupied = (category?.productCount ?? 0) > 0

  const remove = useMutation({
    mutationFn: () =>
      api.delete(
        `/tax-categories/${category?.id}${occupied ? `?moveTo=${encodeURIComponent(moveTo)}` : ''}`,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.taxCategories() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Tax rate deleted')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={category !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {category?.name}?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {occupied ? (
            <>
              <p className="text-sm">
                {category?.productCount}{' '}
                {category?.productCount === 1 ? 'product is' : 'products are'} filed here. Choose
                where they should go.
              </p>
              <Field label="Move products to" required>
                {(props) => (
                  <Select value={moveTo} onValueChange={setMoveTo}>
                    <SelectTrigger {...props}>
                      <SelectValue placeholder="Choose a rate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NON_TAXABLE_MOVE}>Non-taxable</SelectItem>
                      {others.map((other) => (
                        <SelectItem key={other.id} value={other.id}>
                          {other.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </>
          ) : (
            <p className="text-sm">Nothing is filed here, so nothing else changes.</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => remove.mutate()}
            disabled={remove.isPending || (occupied && !moveTo)}
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Whether the vending fleet updates itself (t0660). Off by default — turning
 * it on doesn't push anything immediately, it just means the heartbeat
 * response starts carrying a directive whenever a tablet is behind the
 * published release, and each tablet installs that on its own within about a
 * minute.
 */
function TabletUpdatesCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.storeSettings(),
    queryFn: () => api.get<StoreSettingsInput>('/settings/store'),
  })

  const save = useMutation({
    mutationFn: (tabletAutoUpdate: boolean) => api.patch('/settings/store', { tabletAutoUpdate }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeSettings() })
      toast.success('Saved')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Tablet software updates</CardTitle>
        <CardDescription>
          When on, every vending tablet installs each published release on its own, within about a
          minute of it appearing — no one needs to visit the machine or open this dashboard. Off by
          default; devices can still be updated individually from a machine's Devices tab, or from
          Downloads for the whole fleet.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Update tablets automatically</span>
          <Switch
            checked={data?.tabletAutoUpdate ?? false}
            disabled={isLoading || save.isPending}
            onCheckedChange={(checked) => save.mutate(checked)}
          />
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * What a tablet captures by itself when checkout fails (t1073). Both are off
 * until someone turns them on here: the tablet is told on every heartbeat, and
 * the server refuses an upload for a switch that is off, so a tablet running
 * stale settings cannot capture anyway. Staff can still ask a tablet for a
 * photo or screen capture by hand from the Cameras page whatever these say.
 */
function CheckoutErrorCapturesCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.storeSettings(),
    queryFn: () => api.get<StoreSettingsInput>('/settings/store'),
  })

  const save = useMutation({
    mutationFn: (patch: {
      tabletErrorCaptureScreenshots?: boolean
      tabletErrorCapturePhotos?: boolean
    }) => api.patch('/settings/store', patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeSettings() })
      toast.success('Saved')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Checkout error captures</CardTitle>
        <CardDescription>
          When checkout fails on a vending tablet, it can send the back office a picture of what
          went wrong. Captures appear on the Cameras page, can be attached to a support ticket, and
          are kept for 90 days unless attached. Viewing one is recorded in the audit log. Takes
          effect within about a minute of the tablet's next check-in, and only on tablets running a
          build that supports it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Capture a screenshot of the checkout screen</span>
          <Switch
            checked={data?.tabletErrorCaptureScreenshots ?? false}
            disabled={isLoading || save.isPending}
            onCheckedChange={(checked) => save.mutate({ tabletErrorCaptureScreenshots: checked })}
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">
            Also photograph the customer with the tablet's front camera
          </span>
          <Switch
            checked={data?.tabletErrorCapturePhotos ?? false}
            disabled={isLoading || save.isPending}
            onCheckedChange={(checked) => save.mutate({ tabletErrorCapturePhotos: checked })}
          />
        </div>
        <p className="text-xs text-foreground-muted">
          The photo option captures a customer's face. Leave it off unless the machine has signage
          saying so and you have decided how long to keep the pictures.
        </p>
      </CardContent>
    </Card>
  )
}

/**
 * The vending tablet's admin-menu bypass code (t0611) — one shared numeric
 * code, so a technician standing at a machine can get into the admin menu
 * without an email round-trip. It has no identity of its own, so every
 * action it authorizes borrows the account chosen below.
 */
function VendingAdminCodeCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.storeSettings(),
    queryFn: () => api.get<StoreSettingsInput>('/settings/store'),
  })

  const { data: staffList } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffOption[]>('/staff'),
  })
  const staffOptions = (staffList ?? []).filter((s) => s.isActive)

  const [code, setCode] = useState('')
  const [actsAs, setActsAs] = useState('')
  const [maxAttempts, setMaxAttempts] = useState('')
  const [lockoutMinutes, setLockoutMinutes] = useState('')

  useEffect(() => {
    if (!data) return
    setActsAs(data.vendingAdminActsAsStaffId ?? '')
    setMaxAttempts(String(data.vendingAdminMaxAttempts))
    setLockoutMinutes(String(data.vendingAdminLockoutMinutes))
  }, [data])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.storeSettings() })

  const setCodeMutation = useMutation({
    mutationFn: () =>
      api.put('/settings/vending-admin-code', { code, actsAsStaffId: actsAs || null }),
    onSuccess: async () => {
      await invalidate()
      setCode('')
      toast.success('Admin code set', 'Tablets can use it right away — no app update needed.')
    },
    onError: (error) =>
      toast.error('Could not set the code', error instanceof ApiError ? error.message : undefined),
  })

  const clearCodeMutation = useMutation({
    mutationFn: () => api.delete('/settings/vending-admin-code'),
    onSuccess: async () => {
      await invalidate()
      toast.success('Admin code cleared')
    },
    onError: (error) =>
      toast.error(
        'Could not clear the code',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const saveLimits = useMutation({
    mutationFn: () =>
      api.patch('/settings/store', {
        vendingAdminActsAsStaffId: actsAs || null,
        vendingAdminMaxAttempts: Number(maxAttempts),
        vendingAdminLockoutMinutes: Number(lockoutMinutes),
      }),
    onSuccess: async () => {
      await invalidate()
      toast.success('Saved')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const codeValid = code === '' || /^\d{4,8}$/.test(code)
  const attemptsNum = Number(maxAttempts)
  const lockoutNum = Number(lockoutMinutes)
  const attemptsInvalid = !Number.isInteger(attemptsNum) || attemptsNum < 1 || attemptsNum > 100
  const lockoutInvalid = !Number.isInteger(lockoutNum) || lockoutNum < 1 || lockoutNum > 1440
  const limitsUnchanged = data
    ? attemptsNum === data.vendingAdminMaxAttempts &&
      lockoutNum === data.vendingAdminLockoutMinutes &&
      (actsAs || null) === data.vendingAdminActsAsStaffId
    : true

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Vending tablet admin code</CardTitle>
        <CardDescription>
          A shared numeric code a technician types on a vending tablet instead of signing in with
          email — press and hold the top-left corner of the tablet screen for 10 seconds, then enter
          it. It still works right after the app is force-closed and reopened, with no emailed code
          in the way. On a tablet with no connection the same code opens the Wi-Fi settings so it
          can be put back online — save the code again once so every tablet learns it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-md border border-border p-2.5 text-sm">
          <span>
            {isLoading ? 'Loading…' : data?.vendingAdminCodeSet ? 'A code is set' : 'No code set'}
          </span>
          {data?.vendingAdminCodeSet ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => clearCodeMutation.mutate()}
              loading={clearCodeMutation.isPending}
            >
              Clear
            </Button>
          ) : null}
        </div>

        <Field
          label="Tablet actions are recorded as"
          hint="The staff account a technician using this code is signed in as."
        >
          {(props) => (
            <Select value={actsAs || undefined} onValueChange={setActsAs}>
              <SelectTrigger {...props}>
                <SelectValue placeholder="Choose a staff account" />
              </SelectTrigger>
              <SelectContent>
                {staffOptions.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field
          label="Set or replace the code"
          hint="Numbers only, 4–8 digits."
          error={!codeValid ? 'Numbers only, 4–8 digits' : undefined}
        >
          {(props) => (
            <div className="flex gap-2">
              <Input
                {...props}
                inputMode="numeric"
                value={code}
                invalid={!codeValid}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              />
              <Button
                variant="primary"
                onClick={() => setCodeMutation.mutate()}
                disabled={!code || !codeValid || !actsAs || setCodeMutation.isPending}
                loading={setCodeMutation.isPending}
              >
                Set
              </Button>
            </div>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Wrong tries before lockout"
            error={attemptsInvalid ? 'A whole number between 1 and 100' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                max={100}
                inputMode="numeric"
                invalid={attemptsInvalid}
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Lockout length (minutes)"
            error={lockoutInvalid ? 'A whole number between 1 and 1440' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                max={1440}
                inputMode="numeric"
                invalid={lockoutInvalid}
                value={lockoutMinutes}
                onChange={(e) => setLockoutMinutes(e.target.value)}
              />
            )}
          </Field>
        </div>

        <Button
          variant="primary"
          onClick={() => saveLimits.mutate()}
          disabled={attemptsInvalid || lockoutInvalid || limitsUnchanged || saveLimits.isPending}
        >
          {saveLimits.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}
